import React from "react";
import { Box, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view";
import ArticleIcon from "@mui/icons-material/Article";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { useTreeViewContext } from "../TreeViewContext";
import { drawingNodeId, drawingsFolderNodeId } from "../utils/treeNodeIds";
import {
  drawingLabel,
  drawingNumberOf,
  drawingRevOf,
  wbsLabel,
} from "../utils/treeLabels";

// One 2D drawing filed under a WBS (wbs.drawings). Clicking it resolves
// through nodeTargetRef and navigates straight to /plant/:plantId/drawing/:drawingId
// (BomPage) with no confirmation - see useTreeSelection.
export const DrawingNode = ({ plant, wbs, drawing, index = 0 }) => {
  const { nodeTargetRef } = useTreeViewContext();
  const id = drawingNodeId(wbs.id, drawing?.id ?? `${drawingNumberOf(drawing)}-${index}`);
  const label = drawingLabel(drawing);

  nodeTargetRef.current[id] = {
    kind: "drawing",
    plantId: plant.id,
    projectId: wbs.id,
    projectName: wbsLabel(wbs),
    drawingId: drawing?.id,
    dwgNumber: drawingNumberOf(drawing),
    revision: drawingRevOf(drawing),
  };

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<ArticleIcon fontSize="inherit" sx={{ color: "warning.main" }} />}
      label={
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          title={label}
          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
        >
          {label}
        </Typography>
      }
    />
  );
};

// The "2D Drawings" folder under one WBS. Like the procurement folders it
// carries no selection target - a click only expands it.
export const DrawingsFolderNode = ({ plant, wbs, drawings }) => {
  const id = drawingsFolderNodeId(wbs.id);
  const list = Array.isArray(drawings) ? drawings : [];

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={
        <LibraryBooksIcon fontSize="inherit" sx={{ color: "text.disabled" }} />
      }
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
          >
            2D Drawings
          </Typography>
          <Typography variant="caption" color="text.disabled">
            ({list.length})
          </Typography>
        </Box>
      }
    >
      {list.length > 0 ? (
        list.map((drawing, index) => (
          <DrawingNode
            key={drawing?.id ?? `${drawingNumberOf(drawing)}-${index}`}
            plant={plant}
            wbs={wbs}
            drawing={drawing}
            index={index}
          />
        ))
      ) : (
        <TreeItem
          key={`${id}-empty`}
          nodeId={`${id}-empty`}
          label={
            <Typography variant="caption" color="text.secondary">
              No 2D drawings
            </Typography>
          }
        />
      )}
    </TreeItem>
  );
};
